/**
 * Monthly package quotas (e.g. blog posts per month).
 *
 * Source: GET /api/teams/quota-usage (live usage, limits of the effective package, platform restriction, mode).
 * The same query feeds usePlatformAccess. Nothing is locked in the UI unless the backend mode is 'enforce';
 * a null limit (or a missing quota) means unlimited.
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { teamQueryKeys } from '@/lib/queryKeys';
import type { GatingMode, QuotaUsageResponse } from '@/types/package';

export const quotaUsageKey = (teamId: number | null) =>
  [...teamQueryKeys.all(teamId), 'quota-usage'] as const;

const fetchQuotaUsage = async (): Promise<QuotaUsageResponse> => {
  const response = await apiClient.get<{ success: boolean; data: QuotaUsageResponse }>('/api/teams/quota-usage');
  return response.data.data;
};

/** Shared query. Usage is live, so it is refetched on every mount; a failure never retries (circuit breaker). */
export const useQuotaUsage = () => {
  const { teamId, hasTeam } = useEffectiveTeamId();
  return useQuery({
    queryKey: quotaUsageKey(teamId),
    queryFn: fetchQuotaUsage,
    enabled: hasTeam,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
};

/** Call after an action that consumes quota (e.g. a blog publish) so every usage line is fresh. */
export const useInvalidateQuotaUsage = (): (() => void) => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: quotaUsageKey(teamId) });
  }, [queryClient, teamId]);
};

export interface QuotaState {
  isLoading: boolean;
  /** The quota is known and has a finite limit (show the usage line) */
  hasLimit: boolean;
  used: number;
  /** null = unlimited or unknown */
  limit: number | null;
  remaining: number | null;
  resetsAt: string | null;
  /** used >= limit (informational; only `locked` should block UI) */
  isAtLimit: boolean;
  /** At the limit AND the backend enforces: the action will be refused */
  locked: boolean;
  mode: GatingMode;
  /** The backend enforces package limits (mode 'enforce'). Show NO limit / usage hint unless true. */
  enforced: boolean;
}

export const useQuota = (quotaKey: string): QuotaState => {
  const { data, isLoading } = useQuotaUsage();
  const entry = data?.quotas?.[quotaKey];
  const mode: GatingMode = data?.mode ?? 'off';
  const limit = entry?.limit ?? null;
  // used is null when the backend does not count (mode 'off'); every usage line is gated on `enforced`, so
  // this 0 is never shown, and `locked` also needs mode 'enforce'.
  const used = entry?.used ?? 0;
  const isAtLimit = limit !== null && used >= limit;
  return {
    isLoading,
    hasLimit: limit !== null,
    used,
    limit,
    remaining: limit === null ? null : Math.max(0, limit - used),
    resetsAt: entry?.resetsAt ?? null,
    isAtLimit,
    locked: isAtLimit && mode === 'enforce',
    mode,
    enforced: mode === 'enforce',
  };
};

export default useQuota;
